// =====================================================================
// ChatWidget.jsx - Fixed Version
// الإصلاحات:
// 1. تصحيح اتجاه الرسائل مع RTL/LTR (الزائر يمين دايماً، الأدمن يسار دايماً)
// 2. تصحيح endpoints للـ API
// 3. إضافة polling كل 4 ثواني لاستقبال الرسائل الجديدة
// 4. تصحيح مشكلة إنشاء الغرف
// 5. واجهة نظيفة ومنظمة
// =====================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, X, Send, Minimize2,
  Lock, Info, AlertCircle, LogOut, Power, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import publicChatApi from '../services/publicChatApi';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const POLL_INTERVAL = 4000; // 4 ثواني

const ChatWidget = () => {
  const { language } = useLanguage();
  const { user }     = useAuth();
  const location     = useLocation();

  // إخفاء الويدجت في صفحات الأدمن
  const isAdminPage = location.pathname.startsWith('/admin');
  if (isAdminPage || user) return null;

  return <ChatWidgetCore language={language} />;
};

// ── Core Component ────────────────────────────────────────────────────
const ChatWidgetCore = ({ language }) => {
  const isRTL = language === 'ar';

  const [isOpen,        setIsOpen]        = useState(false);
  const [isMinimized,   setIsMinimized]   = useState(false);
  const [messages,      setMessages]      = useState([]);
  const [newMessage,    setNewMessage]    = useState('');
  const [visitorName,   setVisitorName]   = useState('');
  const [visitorEmail,  setVisitorEmail]  = useState('');
  const [roomNumber,    setRoomNumber]    = useState('');
  const [password,      setPassword]      = useState('');
  const [chatStarted,   setChatStarted]   = useState(false);
  const [roomId,        setRoomId]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [sending,       setSending]       = useState(false);
  const [error,         setError]         = useState('');
  const [showInfo,      setShowInfo]      = useState(true);
  const [isRoomEnded,   setIsRoomEnded]   = useState(false);
  const [mode,          setMode]          = useState('create'); // 'create' | 'join'
  const [showMenu,      setShowMenu]      = useState(false);
  const [unreadCount,   setUnreadCount]   = useState(0);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const pollRef        = useRef(null);
  const lastMsgCount   = useRef(0);

  // ── Translations ──────────────────────────────────────────────────

  const t = useMemo(() => ({
    ar: {
      title:              'الدعم المباشر',
      subtitle:           'نحن هنا لمساعدتك',
      online:             'متاح الآن',
      namePlaceholder:    'اسمك الكامل',
      emailPlaceholder:   'بريدك الإلكتروني',
      roomPlaceholder:    'رقم الغرفة (مثال: 101)',
      passwordPlaceholder:'كلمة المرور (اختياري)',
      msgPlaceholder:     'اكتب رسالتك...',
      createRoom:         'غرفة جديدة',
      joinRoom:           'دخول غرفة',
      startChat:          'ابدأ المحادثة',
      join:               'دخول',
      send:               'إرسال',
      connecting:         'جاري الاتصال...',
      roomInUse:          'رقم الغرفة مستخدم، اختر رقماً آخر',
      roomNotFound:       'الغرفة غير موجودة أو منتهية',
      passwordRequired:   'كلمة المرور مطلوبة',
      invalidPassword:    'كلمة المرور غير صحيحة',
      invalidEmail:       'البريد الإلكتروني غير مسجل في هذه الغرفة',
      roomEnded:          '⛔ تم إنهاء المحادثة',
      endChat:            'إنهاء المحادثة',
      confirmEnd:         'هل تريد إنهاء المحادثة؟',
      logout:             'الخروج',
      newChat:            'محادثة جديدة',
      room:               'غرفة',
      you:                'أنت',
      support:            'الدعم',
      refresh:            'تحديث',
      infoTitle:          'كيفية الاستخدام',
      infoItems: [
        'أدخل بياناتك واختر رقم غرفة من اختيارك',
        'يمكنك حماية الغرفة بكلمة مرور',
        'للعودة لمحادثتك، اختر "دخول غرفة"',
      ],
      errorGeneric: 'حدث خطأ، حاول مرة أخرى',
      missingFields: 'من فضلك أدخل جميع البيانات المطلوبة',
    },
    en: {
      title:              'Live Support',
      subtitle:           "We're here to help",
      online:             'Online now',
      namePlaceholder:    'Your full name',
      emailPlaceholder:   'Your email',
      roomPlaceholder:    'Room number (e.g., 101)',
      passwordPlaceholder:'Password (optional)',
      msgPlaceholder:     'Type your message...',
      createRoom:         'New Room',
      joinRoom:           'Join Room',
      startChat:          'Start Chat',
      join:               'Join',
      send:               'Send',
      connecting:         'Connecting...',
      roomInUse:          'Room number in use, choose another',
      roomNotFound:       'Room not found or ended',
      passwordRequired:   'Password required',
      invalidPassword:    'Incorrect password',
      invalidEmail:       'Email not registered in this room',
      roomEnded:          '⛔ Chat has ended',
      endChat:            'End Chat',
      confirmEnd:         'End this conversation?',
      logout:             'Logout',
      newChat:            'New Chat',
      room:               'Room',
      you:                'You',
      support:            'Support',
      refresh:            'Refresh',
      infoTitle:          'How to Use',
      infoItems: [
        'Enter your info and choose a room number',
        'You can protect your room with a password',
        'To return to your chat, use "Join Room"',
      ],
      errorGeneric: 'Something went wrong, please try again',
      missingFields: 'Please fill in all required fields',
    },
  }[language] || {}), [language]);

  // ── Helpers ───────────────────────────────────────────────────────

  const formatTime = useCallback((date) => {
    try {
      const d = date?._seconds ? new Date(date._seconds * 1000) : new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(isRTL ? 'ar-SA' : 'en-US', {
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return ''; }
  }, [isRTL]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // ── Load Messages ─────────────────────────────────────────────────

  const loadMessages = useCallback(async (id, email, pass, silent = false) => {
    if (!id || !email) return;
    try {
      const params = { visitorEmail: email };
      if (pass) params.password = pass;

      const response = await publicChatApi.get(`/chat/rooms/${id}/messages`, { params });
      const data = response.data;

      if (data.success) {
        if (data.isEnded) setIsRoomEnded(true);

        const newMsgs = data.messages || [];

        // حساب الرسائل غير المقروءة لو الويدجت مش مفتوح
        if (!isOpen || isMinimized) {
          const adminMsgs = newMsgs.filter(m => m.sender === 'admin').length;
          if (adminMsgs > lastMsgCount.current) {
            setUnreadCount(prev => prev + (adminMsgs - lastMsgCount.current));
          }
          lastMsgCount.current = adminMsgs;
        }

        setMessages(newMsgs);
      }
    } catch (e) {
      if (e.response?.status === 404) logout();
    }
  }, [isOpen, isMinimized]); // eslint-disable-line

  // ── Polling ───────────────────────────────────────────────────────

  const startPolling = useCallback((id, email, pass) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      loadMessages(id, email, pass, true);
    }, POLL_INTERVAL);
  }, [loadMessages]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    stopPolling();
    localStorage.removeItem('visitorChat');
    setChatStarted(false);
    setRoomId(null);
    setMessages([]);
    setIsRoomEnded(false);
    setError('');
    setPassword('');
    setShowMenu(false);
    setUnreadCount(0);
    lastMsgCount.current = 0;
  }, [stopPolling]);

  // ── Effects ───────────────────────────────────────────────────────

  // Auto scroll
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // استعادة الجلسة المحفوظة
  useEffect(() => {
    const saved = localStorage.getItem('visitorChat');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.roomId && data.visitorEmail) {
          setRoomId(data.roomId);
          setVisitorName(data.visitorName || '');
          setVisitorEmail(data.visitorEmail);
          setRoomNumber(data.roomNumber || '');
          setPassword(data.password || '');
          setChatStarted(true);
        }
      } catch {
        localStorage.removeItem('visitorChat');
      }
    }
  }, []);

  // تحميل الرسائل وبدء الـ polling عند فتح الشات
  useEffect(() => {
    if (chatStarted && roomId && visitorEmail) {
      loadMessages(roomId, visitorEmail, password);
      startPolling(roomId, visitorEmail, password);
    }
    return () => stopPolling();
  }, [chatStarted, roomId, visitorEmail, password]); // eslint-disable-line

  // إيقاف الـ polling لما الشات مغلق أو minimized
  useEffect(() => {
    if (!isOpen || isMinimized) {
      stopPolling();
    } else if (chatStarted && roomId && visitorEmail) {
      startPolling(roomId, visitorEmail, password);
    }
  }, [isOpen, isMinimized, chatStarted, roomId, visitorEmail, password]); // eslint-disable-line

  // تصفير الـ unread عند الفتح
  useEffect(() => {
    if (isOpen && !isMinimized) setUnreadCount(0);
  }, [isOpen, isMinimized]);

  // Focus input
  useEffect(() => {
    if (chatStarted && isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [chatStarted, isOpen, isMinimized]);

  // ── Create Room ───────────────────────────────────────────────────

  const createRoom = useCallback(async () => {
    if (!visitorName.trim() || !visitorEmail.trim() || !roomNumber.trim()) {
      setError(t.missingFields);
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await publicChatApi.post('/chat/rooms', {
        visitorName:  visitorName.trim(),
        visitorEmail: visitorEmail.trim(),
        roomNumber:   roomNumber.trim(),
        password:     password.trim() || undefined,
      });

      const data = response.data;
      if (data.success) {
        const newRoomId = data.roomId;
        setRoomId(newRoomId);
        setChatStarted(true);
        localStorage.setItem('visitorChat', JSON.stringify({
          roomId:       newRoomId,
          visitorName:  visitorName.trim(),
          visitorEmail: visitorEmail.trim(),
          roomNumber:   roomNumber.trim(),
          password:     password.trim() || '',
        }));
        await loadMessages(newRoomId, visitorEmail.trim(), password.trim());
        startPolling(newRoomId, visitorEmail.trim(), password.trim());
      }
    } catch (e) {
      const errCode = e.response?.data?.error;
      const errMap = {
        ROOM_IN_USE:    t.roomInUse,
        INVALID_EMAIL:  t.invalidEmail,
        MISSING_FIELDS: t.missingFields,
      };
      setError(errMap[errCode] || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  }, [visitorName, visitorEmail, roomNumber, password, t, loadMessages, startPolling]);

  // ── Join Room ─────────────────────────────────────────────────────

  const joinRoom = useCallback(async () => {
    if (!roomNumber.trim() || !visitorEmail.trim()) {
      setError(t.missingFields);
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await publicChatApi.post('/chat/rooms/join', {
        roomNumber:   roomNumber.trim(),
        visitorEmail: visitorEmail.trim(),
        password:     password.trim() || undefined,
      });

      const data = response.data;
      if (data.success) {
        const { roomId: id, visitorName: vName } = data;
        const finalName = vName || visitorName;
        setRoomId(id);
        setVisitorName(finalName);
        setChatStarted(true);
        localStorage.setItem('visitorChat', JSON.stringify({
          roomId:       id,
          visitorName:  finalName,
          visitorEmail: visitorEmail.trim(),
          roomNumber:   roomNumber.trim(),
          password:     password.trim() || '',
        }));
        await loadMessages(id, visitorEmail.trim(), password.trim());
        startPolling(id, visitorEmail.trim(), password.trim());
      }
    } catch (e) {
      const errCode = e.response?.data?.error;
      const errMap = {
        ROOM_NOT_FOUND:  t.roomNotFound,
        PASSWORD_REQUIRED: t.passwordRequired,
        INVALID_PASSWORD:  t.invalidPassword,
        INVALID_EMAIL:     t.invalidEmail,
      };
      setError(errMap[errCode] || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  }, [roomNumber, visitorEmail, password, visitorName, t, loadMessages, startPolling]);

  // ── Send Message ──────────────────────────────────────────────────

  const sendMessage = useCallback(async (e) => {
    e?.preventDefault();
    const content = newMessage.trim();
    if (!content || !roomId || sending || isRoomEnded) return;

    setNewMessage('');
    setSending(true);

    // رسالة مؤقتة للـ optimistic UI
    const tempMsg = {
      id:        `temp-${Date.now()}`,
      content,
      sender:    'visitor',
      createdAt: new Date().toISOString(),
      isTemp:    true,
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      await publicChatApi.post(`/chat/rooms/${roomId}/messages`, {
        content,
        sender:       'visitor',
        visitorEmail,
        password:     password || undefined,
      });

      // جلب الرسائل لتحديث الـ temp message
      await loadMessages(roomId, visitorEmail, password, true);
    } catch (e) {
      if (e.response?.data?.error === 'ROOM_ENDED') {
        setIsRoomEnded(true);
      }
      // إزالة الرسالة المؤقتة وإعادة النص
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      setNewMessage(content);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [newMessage, roomId, sending, isRoomEnded, visitorEmail, password, loadMessages]);

  // ── End Chat ──────────────────────────────────────────────────────

  const endChat = useCallback(async () => {
    if (!window.confirm(t.confirmEnd)) return;
    try {
      await publicChatApi.put(`/chat/rooms/${roomId}/visitor-end`, {
        visitorEmail,
        password: password || undefined,
      });
      setIsRoomEnded(true);
      stopPolling();
      localStorage.removeItem('visitorChat');
    } catch (error) {
      console.error('Error ending chat:', error);
    }
    setShowMenu(false);
  }, [roomId, visitorEmail, password, t.confirmEnd, stopPolling]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const refreshMessages = useCallback(() => {
    if (roomId && visitorEmail) {
      loadMessages(roomId, visitorEmail, password);
    }
  }, [roomId, visitorEmail, password, loadMessages]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setUnreadCount(0);
  }, []);

  // ── Render ────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Toggle Button ── */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOpen}
            className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-500 text-white p-4 rounded-full shadow-2xl"
            style={{ boxShadow: '0 8px 32px rgba(6,182,212,0.5)' }}
          >
            <MessageCircle className="h-6 w-6" />
            {/* نقطة أونلاين */}
            <span className="absolute top-0.5 right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            {/* عداد الرسائل غير المقروءة */}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -left-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Chat Window ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.92 }}
            animate={{
              opacity: 1, y: 0, scale: 1,
              height: isMinimized ? 'auto' : 'min(600px, 90vh)',
            }}
            exit={{ opacity: 0, y: 40, scale: 0.92 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-w-md bg-white dark:bg-gray-900 rounded-2xl overflow-hidden flex flex-col"
            style={{ boxShadow: '0 16px 56px rgba(0,0,0,0.25)', maxHeight: '90vh' }}
          >
            {/* ── Header ── */}
            <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 text-white px-4 py-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                {/* Info */}
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <MessageCircle className="h-5 w-5" />
                    </div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm leading-tight">{t.title}</h3>
                    <p className="text-[11px] text-cyan-200 flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                      {chatStarted ? `${t.room} #${roomNumber}` : t.online}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {chatStarted && (
                    <>
                      <button
                        onClick={refreshMessages}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                        title={t.refresh}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setShowMenu(m => !m)}
                          className="p-2 hover:bg-white/20 rounded-full transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>
                        <AnimatePresence>
                          {showMenu && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.92, y: -5 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.92, y: -5 }}
                              className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-2xl py-1.5 z-50 border border-gray-100 dark:border-gray-700 overflow-hidden"
                            >
                              {!isRoomEnded && (
                                <button
                                  onClick={endChat}
                                  className="w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2.5 transition-colors"
                                >
                                  <Power className="w-4 h-4" />
                                  {t.endChat}
                                </button>
                              )}
                              <button
                                onClick={logout}
                                className="w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
                              >
                                <LogOut className="w-4 h-4" />
                                {t.logout}
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </>
                  )}
                  <button
                    onClick={() => setIsMinimized(m => !m)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Body ── */}
            {!isMinimized && (
              <>
                {!chatStarted ? (
                  /* ── Start Form ── */
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
                    {/* Info box */}
                    <AnimatePresence>
                      {showInfo && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          className="p-3.5 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-100 dark:border-cyan-800"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5 text-cyan-700 dark:text-cyan-300">
                              <Info className="h-4 w-4" />
                              <span className="text-xs font-semibold">{t.infoTitle}</span>
                            </div>
                            <button onClick={() => setShowInfo(false)}>
                              <X className="h-3.5 w-3.5 text-cyan-400 hover:text-cyan-600" />
                            </button>
                          </div>
                          <ul className="space-y-1.5">
                            {t.infoItems.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-cyan-600 dark:text-cyan-300">
                                <span className="bg-cyan-200 dark:bg-cyan-800 text-cyan-700 dark:text-cyan-200 rounded-full w-4 h-4 flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5 font-bold">
                                  {i + 1}
                                </span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Error */}
                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400"
                        >
                          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span className="text-xs font-medium">{error}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Mode Toggle */}
                    <div className="flex bg-gray-200 dark:bg-gray-800 rounded-xl p-1 gap-1">
                      {['create', 'join'].map((m) => (
                        <button
                          key={m}
                          onClick={() => { setMode(m); setError(''); }}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                            mode === m
                              ? 'bg-gradient-to-r from-cyan-600 to-teal-500 text-white shadow-md'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          {m === 'create' ? t.createRoom : t.joinRoom}
                        </button>
                      ))}
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-2.5">
                      {mode === 'create' && (
                        <input
                          type="text"
                          value={visitorName}
                          onChange={e => setVisitorName(e.target.value)}
                          placeholder={t.namePlaceholder}
                          dir={isRTL ? 'rtl' : 'ltr'}
                          className="w-full px-3.5 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all text-sm outline-none"
                        />
                      )}

                      <input
                        type="email"
                        value={visitorEmail}
                        onChange={e => setVisitorEmail(e.target.value)}
                        placeholder={t.emailPlaceholder}
                        dir="ltr"
                        className="w-full px-3.5 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all text-sm outline-none"
                      />

                      <input
                        type="text"
                        value={roomNumber}
                        onChange={e => setRoomNumber(e.target.value)}
                        placeholder={t.roomPlaceholder}
                        dir="ltr"
                        className="w-full px-3.5 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all text-sm outline-none"
                      />

                      <div className="relative">
                        <input
                          type="password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder={t.passwordPlaceholder}
                          dir="ltr"
                          className="w-full pl-3.5 pr-10 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all text-sm outline-none"
                        />
                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      </div>

                      <button
                        onClick={mode === 'create' ? createRoom : joinRoom}
                        disabled={loading || (mode === 'create'
                          ? (!visitorName.trim() || !visitorEmail.trim() || !roomNumber.trim())
                          : (!visitorEmail.trim() || !roomNumber.trim())
                        )}
                        className="w-full py-3.5 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 hover:from-blue-700 hover:via-cyan-700 hover:to-teal-600 disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-500 text-white rounded-xl font-bold text-sm transition-all shadow-lg disabled:shadow-none"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            {t.connecting}
                          </span>
                        ) : mode === 'create' ? t.startChat : t.join}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Chat Area ── */
                  <>
                    {/* Room Banner */}
                    <div className="px-4 py-2 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-gray-800 dark:to-gray-800 flex items-center justify-between border-b border-cyan-100 dark:border-gray-700 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">{t.room}</span>
                        <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-900/30 px-2 py-0.5 rounded-lg">
                          #{roomNumber}
                        </span>
                        {visitorName && (
                          <span className="text-[11px] text-gray-500 dark:text-gray-400">· {visitorName}</span>
                        )}
                      </div>
                      {isRoomEnded && (
                        <span className="text-[11px] font-semibold text-red-500 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded-full">
                          {t.roomEnded}
                        </span>
                      )}
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
                      {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="w-14 h-14 bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                              <MessageCircle className="w-7 h-7 text-cyan-500" />
                            </div>
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                              {isRTL ? 'ابدأ المحادثة الآن' : 'Start the conversation'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        messages.map((msg, index) => {
                          const isVisitor = msg.sender === 'visitor';
                          const isSystem  = msg.sender === 'system';

                          if (isSystem) {
                            return (
                              <div key={msg.id || index} className="flex justify-center my-3">
                                <span className="text-[11px] px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full font-medium">
                                  {msg.content}
                                </span>
                              </div>
                            );
                          }

                          // ── رسالة الزائر: دايماً يمين ──
                          // ── رسالة الأدمن: دايماً يسار ──
                          // (بغض النظر عن RTL/LTR)
                          const isRight = isVisitor;

                          return (
                            <motion.div
                              key={msg.id || index}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className="max-w-[80%]">
                                {/* Label */}
                                <p className={`text-[10px] font-semibold mb-1 px-1 ${isRight ? 'text-right text-cyan-600 dark:text-cyan-400' : 'text-left text-gray-500 dark:text-gray-400'}`}>
                                  {isVisitor ? t.you : t.support}
                                </p>
                                {/* Bubble */}
                                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                  isRight
                                    ? 'bg-gradient-to-br from-cyan-600 to-teal-500 text-white rounded-br-sm'
                                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-100 dark:border-gray-700 rounded-bl-sm shadow-sm'
                                } ${msg.isTemp ? 'opacity-70' : ''}`}>
                                  {msg.content}
                                </div>
                                {/* Time */}
                                <p className={`text-[10px] mt-1 px-1 text-gray-400 dark:text-gray-500 ${isRight ? 'text-right' : 'text-left'}`}>
                                  {formatTime(msg.createdAt)}
                                </p>
                              </div>
                            </motion.div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
                      {isRoomEnded ? (
                        <div className="text-center py-2">
                          <p className="text-xs text-gray-400 mb-2">{t.roomEnded}</p>
                          <button
                            onClick={logout}
                            className="text-xs text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 font-semibold underline"
                          >
                            {t.newChat}
                          </button>
                        </div>
                      ) : (
                        <form onSubmit={sendMessage} className="flex items-center gap-2">
                          <input
                            ref={inputRef}
                            type="text"
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={t.msgPlaceholder}
                            disabled={sending}
                            dir={isRTL ? 'rtl' : 'ltr'}
                            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 rounded-full text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 transition-all"
                          />
                          <button
                            type="submit"
                            disabled={!newMessage.trim() || sending}
                            className="bg-gradient-to-r from-cyan-600 to-teal-500 hover:from-cyan-700 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-400 text-white p-2.5 rounded-full transition-all shadow-md disabled:shadow-none flex-shrink-0"
                          >
                            {sending ? (
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : (
                              <Send className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
                            )}
                          </button>
                        </form>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;